import { useState } from 'react'
import MemoryUsedNote from '../shared/MemoryUsedNote'
import { useTranslation } from 'react-i18next'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

import MacroTile from '../shared/MacroTile'
import { formatCalories, formatGrams } from '../../utils/format'

export default function FoodAnalysisReview({ analysis, onAccept, onReject, onReanalyzeWithHint, onNextPhoto }) {
  const { t, i18n } = useTranslation()
  const [expandedFoods, setExpandedFoods] = useState({})
  const [userHint, setUserHint] = useState('')

  if (!analysis) return null

  const locale = i18n.language || 'en'

  const toggleExpanded = (index) => {
    setExpandedFoods(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  // Calculate total confidence
  const avgConfidence = analysis.confidence || 0
  const confidenceColor = avgConfidence >= 0.85 ? 'text-green-600' : avgConfidence >= 0.7 ? 'text-yellow-600' : 'text-red-600'
  const confidenceBg = avgConfidence >= 0.85 ? 'bg-green-50' : avgConfidence >= 0.7 ? 'bg-yellow-50' : 'bg-red-50'

  return (
    <div className="space-y-6">
      {/* Analysis Header */}
      <div className="bg-surface-container rounded-lg border-2 border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Food Analysis Results</h2>
          <div className={`px-3 py-1 rounded-full ${confidenceBg}`}>
            <span className={`text-sm font-medium ${confidenceColor}`}>
              {(avgConfidence * 100).toFixed(0)}% Confidence
            </span>
          </div>
        </div>

        {/* Meal Total Summary — responsive 1/2/4 grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <MacroTile
            label={t('nutrition.macros.calories')}
            value={formatCalories(analysis.meal_total?.calories ?? 0, locale)}
            unit={t('nutrition.units.kcal')}
            color="indigo"
          />
          <MacroTile
            label={t('nutrition.macros.protein')}
            value={formatGrams(analysis.meal_total?.protein_g ?? 0, locale)}
            unit={t('nutrition.units.g')}
            color="blue"
          />
          <MacroTile
            label={t('nutrition.macros.carbs')}
            value={formatGrams(analysis.meal_total?.carbohydrates_g ?? 0, locale)}
            unit={t('nutrition.units.g')}
            color="green"
          />
          <MacroTile
            label={t('nutrition.macros.fat')}
            value={formatGrams(analysis.meal_total?.fat_g ?? 0, locale)}
            unit={t('nutrition.units.g')}
            color="amber"
          />
        </div>

        {/* Warnings */}
        {analysis.warnings && analysis.warnings.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800 mb-1">{t('nutrition.review.pleaseReview')}</h3>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {analysis.warnings.map((warning, idx) => (
                    <li key={idx}>• {warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <MemoryUsedNote memories={analysis.memories_used} />

      {/* Identified Foods */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">
          Identified Foods ({analysis.foods_identified?.length || 0})
        </h3>

        {analysis.foods_identified?.map((food, index) => {
          const isExpanded = expandedFoods[index]
          const foodConfidence = food.confidence || 0
          const foodConfidenceColor = foodConfidence >= 0.85 ? 'text-green-600' : foodConfidence >= 0.7 ? 'text-yellow-600' : 'text-red-600'

          // Get database match for this food
          const dbMatch = analysis.database_matches?.[index]
          const bestMatch = dbMatch?.best_match

          return (
            <div key={index} className="bg-surface-container border-2 border-gray-200 rounded-lg overflow-hidden">
              {/* Food Header */}
              <button
                onClick={() => toggleExpanded(index)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className="flex-shrink-0">
                    <div className={`w-2 h-2 rounded-full ${
                      foodConfidence >= 0.85 ? 'bg-green-500' : foodConfidence >= 0.7 ? 'bg-yellow-500' : 'bg-red-500'
                    }`} />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <h4
                      className="text-base font-semibold text-gray-900 truncate"
                      title={food.name}
                    >
                      {food.name}
                    </h4>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                      <span>{food.estimated_weight_g}{t('nutrition.units.g')}</span>
                      <span>•</span>
                      <span>{food.portion_visual}</span>
                      <span>•</span>
                      <span className={foodConfidenceColor}>
                        {(foodConfidence * 100).toFixed(0)}% match
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-bold text-gray-900 tabular-nums">
                      {formatCalories(food.nutritional_summary?.calories ?? 0, locale)} {t('nutrition.units.kcal')}
                    </div>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-400 ml-4" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-4" />
                )}
              </button>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-6 pb-4 border-t border-gray-200">
                  {/* Nutritional Details — 1 col on mobile, 3 on md+ */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                    <div className="flex justify-between md:block">
                      <div className="text-xs text-gray-500">{t('nutrition.macros.protein')}</div>
                      <div className="text-sm font-medium text-gray-900 tabular-nums">
                        {formatGrams(food.nutritional_summary?.protein_g ?? 0, locale)} {t('nutrition.units.g')}
                      </div>
                    </div>
                    <div className="flex justify-between md:block">
                      <div className="text-xs text-gray-500">{t('nutrition.macros.carbs')}</div>
                      <div className="text-sm font-medium text-gray-900 tabular-nums">
                        {formatGrams(food.nutritional_summary?.carbohydrates_g ?? 0, locale)} {t('nutrition.units.g')}
                      </div>
                    </div>
                    <div className="flex justify-between md:block">
                      <div className="text-xs text-gray-500">{t('nutrition.macros.fat')}</div>
                      <div className="text-sm font-medium text-gray-900 tabular-nums">
                        {formatGrams(food.nutritional_summary?.fat_g ?? 0, locale)} {t('nutrition.units.g')}
                      </div>
                    </div>
                  </div>

                  {/* Database Match */}
                  {bestMatch && (
                    <div className="mt-4 bg-indigo-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium text-indigo-900">Database Match</h5>
                        <span className="text-xs text-indigo-600">
                          {(bestMatch.confidence * 100).toFixed(0)}% match ({bestMatch.match_type})
                        </span>
                      </div>
                      <div className="text-sm text-indigo-800">
                        <div className="font-medium">{bestMatch.food_name}</div>
                        {bestMatch.brand && (
                          <div className="text-xs text-indigo-600">{bestMatch.brand}</div>
                        )}
                        <div className="text-xs text-indigo-600 mt-1">
                          Category: {bestMatch.category} • Serving: {bestMatch.serving_size}{bestMatch.serving_unit}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No Match */}
                  {dbMatch && !bestMatch && (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600">
                        <ExclamationTriangleIcon className="h-5 w-5 text-gray-400 inline mr-2" />
                        No matching food found in database
                      </div>
                    </div>
                  )}

                  {/* Additional Details */}
                  {food.preparation_method && (
                    <div className="mt-3 text-sm text-gray-600">
                      <span className="font-medium">Preparation:</span> {food.preparation_method}
                    </div>
                  )}
                  {food.brand && (
                    <div className="mt-1 text-sm text-gray-600">
                      <span className="font-medium">Brand:</span> {food.brand}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Processing Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            <span className="font-medium">Processing Time:</span>{' '}
            {(analysis.processing_time_ms / 1000).toFixed(2)}s
          </div>
          <div>
            <span className="font-medium">API Cost:</span> ${analysis.api_cost?.toFixed(4) || 0}
          </div>
          <div>
            <span className="font-medium">Model:</span> {analysis.model_used}
          </div>
        </div>
      </div>

      {/* Batch Info */}
      {analysis.batchInfo && analysis.batchInfo.hasMore && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-indigo-900">Batch Upload</p>
              <p className="text-xs text-indigo-700 mt-1">
                Photo {analysis.batchInfo.currentIndex} of {analysis.batchInfo.totalFiles} analyzed
              </p>
            </div>
            <button
              onClick={() => onNextPhoto && onNextPhoto()}
              className="px-4 py-2 bg-indigo-600 text-on-neutral rounded-lg hover:bg-indigo-700 text-sm font-medium"
            >
              Next Photo →
            </button>
          </div>
        </div>
      )}

      {/* User Hint for Re-analysis */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-blue-900 mb-2">
          {t('nutrition.review.addHint', 'Add details to improve analysis (optional)')}
        </label>
        <input
          type="text"
          value={userHint}
          onChange={(e) => setUserHint(e.target.value)}
          placeholder={t('nutrition.review.hintPlaceholder', "e.g., 'sourdough bread', 'low-fat version', 'homemade'")}
          className="w-full px-3 py-2 border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
        />
        <p className="mt-1 text-xs text-blue-700">
          {t('nutrition.review.hintHelp', 'Enter hints in any language to help identify the food more accurately')}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4">
        <button
          onClick={() => onAccept && onAccept(analysis)}
          className="flex-1 btn-primary flex items-center justify-center"
        >
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          {analysis.batchInfo && analysis.batchInfo.hasMore ? 'Accept & Next Photo' : 'Accept & Log Food'}
        </button>
        <button
          onClick={() => {
            if (userHint && onReanalyzeWithHint) {
              onReanalyzeWithHint(userHint)
            } else if (onReject) {
              onReject()
            }
          }}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center"
        >
          <ArrowPathIcon className="h-5 w-5 mr-2" />
          {userHint ? t('nutrition.review.reanalyzeWithHint', 'Re-analyze with Hint') : t('nutrition.review.tryAgain', 'Try Again')}
        </button>
      </div>
    </div>
  )
}
